import React, { useState, useEffect } from 'react';
import {
  Container,
  Button,
  AppBar,
  Toolbar,
  Typography,
  CircularProgress,
  Table,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  Paper,
  InputAdornment,
  TextField,
  Box,
  Modal
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import base64 from 'base64-js';
import Tooltip from '@mui/material/Tooltip';
import { Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import SearchIcon from "@mui/icons-material/Search";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});


function Bank() {
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [authorization, setAuthorization] = useState([]);
  const [canDelete, setCanDelete] = useState(true);
  const [canEdit, setCanEdit] = useState(true);
  const [canAdd, setCanAdd] = useState(true);
  const { encodedFormId } = useParams();
  const [selectedBank, setSelectedBank] = useState({
    bankId: null,
    bankName: "",
  });
  const [showModal, setShowModal] = useState(false);

  const decodeFormId = (encodedFormId) => {
    const bytes = base64.toByteArray(encodedFormId);
    return new TextDecoder().decode(bytes);
  };

  const formId = decodeFormId(encodedFormId);

  const handleAddBank = () => {
    navigate('/addbank');
  };

  const fetchBankDetails = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');
      setLoadingBarProgress(30);
      const response = await fetch(`${apiUrl}/Bank/GetBank`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const data = await response.json();
        setBanks(data);
        setLoadingBarProgress(100);
      } else {
        console.error('Failed to fetch bank details');
        setLoadingBarProgress(0);
      }
    } catch (error) {
      console.error('API request error:', error);
      setLoadingBarProgress(0);
    }
  };

  const Authorizer = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      setLoadingBarProgress(30);
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          formId,
        }),
      });

      if (response.ok) {
        const responseData = await response.json();
        setAuthorization(responseData);
        const authorizationData = responseData[0];
        setCanDelete(authorizationData.uDelete === 1);
        setCanEdit(authorizationData.uModify === 1);
        setCanAdd(authorizationData.uWrite === 1);
        setLoadingBarProgress(100);
      } else {
        console.error('Authorization request failed');
        setLoadingBarProgress(0);
      }
    } catch (error) {
      console.error('API request error:', error);
      setLoadingBarProgress(0);
    }
  };

  useEffect(() => {
    Authorizer();
    fetchBankDetails();
  }, []);

  useEffect(() => {
    setFilteredData(
      banks.filter((bank) =>
        bank.bankName.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, banks]);

  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );


  const handleEdit = (bankId) => {
    navigate(`/editbank/${bankId}`);
  };

  const handleShow = (bankId, bankName) => {
    if (!canDelete) {
      // alert("You are not authorized to delete payment methods");
      setSnackbarMessage('You are not authorized to delete payment methods');
      setOpenSnackbar(true);
      setSnackbarSeverity("warning")
      return;
    }
    setSelectedBank({ bankId, bankName });
    setShowModal(true);
  };

  const handleClose = () => {
    setSelectedBank({ bankId: null, bankName: "" });
    setShowModal(false);
  };

  const handleDelete = async () => {
    if (!selectedBank.bankId) return;

    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');
      setLoadingBarProgress(30);
      const response = await fetch(
        `${apiUrl}/Bank/Id?Id=${selectedBank.bankId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
        }
      );

      if (response.ok) {
        fetchBankDetails();
        setShowModal(false);
        setLoadingBarProgress(100);
        setSnackbarMessage('Payment Method deleted successfully!');
        setOpenSnackbar(true);
        setSnackbarSeverity("success")
      } else {
        setLoadingBarProgress(0);
        // alert('Failed to delete payment method');
        setSnackbarMessage('Failed to delete payment method');
        setOpenSnackbar(true);
        setSnackbarSeverity("error")
      }
    } catch (error) {
      console.error('API request error:', error);
      setLoadingBarProgress(0);
      // alert('An error occurred. Please try again later.');
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error")
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1); // reset to page 1
  };



  return (
    <Container>
      <AppBar position="static" style={{ backgroundColor: "#0B1F3D" }}>
        <Toolbar>
          <Typography variant="h4" component="div" style={{ fontWeight: 'bold' }}>
            Payment Method
          </Typography>
        </Toolbar>
      </AppBar>

      <Paper elevation={3} style={{ padding: 16, width: '100%', margin: 'auto', marginTop: 6 }}>
        <div className="form-group" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
          <TextField
            variant="outlined"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: "300px" }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
              style: { padding: '0px 8px' },
            }}
            sx={{
              width: 300,
              backgroundColor: '#f9f9f9',
            }}
          />

          <Tooltip title={!canAdd && "You are not Authorized to Add"} arrow><span>

            <Button
              variant="contained"
              color="primary"
              onClick={handleAddBank}
              style={{
                width: "166px",
                height: "50px",
                padding: "10px 0",
                fontWeight: 'bold'
              }}
              disabled={!canAdd}
            >
              Add Payment Mode
            </Button></span></Tooltip>
        </div>

        <Table
          sx={{
            minWidth: 650,
            border: '1px solid #ccc',
            marginTop: '20px',
            '& .MuiTableCell-root': {
              borderRight: '1px solid #ccc',
              fontSize: '1rem',
            },
            '& .MuiTableHead-root .MuiTableCell-root': {
              fontWeight: 'bold',
              backgroundColor: '#f5f5f5',
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell><strong>Payment Method</strong></TableCell>
              <TableCell style={{ textAlign: 'center' }}><strong>Action</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingBarProgress > 0 && loadingBarProgress < 100 ? (
              <TableRow>
                <TableCell colSpan={2} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : paginatedData.length > 0 ? (
              paginatedData.map((bank) => (
                <TableRow key={bank.bankId}>
                  <TableCell>{bank.bankName}</TableCell>
                  <TableCell style={{ textAlign: 'center' }}>
                    <Tooltip
                      title={canEdit ? 'Edit Payment Method' : 'You are not authorized to edit'}
                      arrow
                    >
                      <span>
                        <Button
                          variant="contained"
                          color="warning"
                          onClick={() => handleEdit(bank.bankId)}
                          disabled={!canEdit}
                          startIcon={<EditIcon />}
                          style={{ marginRight: '10px' }}
                        >
                          EDIT
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={canDelete ? 'Delete Payment Method' : 'You are not authorized to delete'}
                      arrow
                    >
                      <span>
                        <Button
                          variant="contained"
                          color="error"
                          onClick={() => handleShow(bank.bankId, bank.bankName)}
                          disabled={!canDelete}
                          startIcon={<DeleteIcon />}
                        >
                          DELETE
                        </Button>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2} align="center">
                  No Payment Methods Found
                </TableCell>
              </TableRow>
            )}
          </TableBody>

        </Table>

        {filteredData.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
            <TextField
              select
              label="Rows per page"
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              sx={{ width: 150 }}
              SelectProps={{
                native: true,
              }}
            >
              {rowsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </TextField>

            <Box>
              <Typography variant="body2" sx={{ display: 'inline-block', marginRight: 2 }}>
                Page {page} of {Math.ceil(filteredData.length / rowsPerPage)}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => handleChangePage(null, page - 1)}
                disabled={page === 1}
                sx={{ mr: 1 }}
              >
                Prev
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleChangePage(null, page + 1)}
                disabled={page >= Math.ceil(filteredData.length / rowsPerPage)}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}

      </Paper>

      {/* Delete Confirmation Modal */}
      <Modal
        open={showModal}
        onClose={handleClose}
        aria-labelledby="delete-modal-title"
        aria-describedby="delete-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ padding: '20px' }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete <strong>{selectedBank?.bankName}</strong>?
            </Typography>

            {/* Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleClose} variant="outlined" sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDelete}
              >
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

    </Container>
  );
}

export default Bank;